import { esVerdadero, tieneValor, formatear } from './catalogo';

const texto = value => tieneValor(value) && ['string', 'number'].includes(typeof value) ? String(value).trim() : '';
const primero = (...values) => values.map(texto).find(Boolean) || '';
const campo = (titulo, valor, ancho = false) => ({ titulo, valor: texto(valor) || 'No registrado', ancho });
const dinero = value => tieneValor(value) && Number.isFinite(Number(value)) ? 'RD$ ' + formatear(value, 'moneda') : 'No registrado';
const nombreCodigo = (nombre, codigo) => nombre && texto(codigo) && texto(nombre).toLowerCase() !== texto(codigo).toLowerCase()
  ? `${nombre} (${codigo})` : primero(nombre, codigo);
const numero = value => tieneValor(value) && Number.isFinite(Number(value)) ? Number(value) : null;

export function fechaDetalle(valor, soloFecha = false) {
  const t = texto(valor);
  if (!t) return '';
  if (/^\d{2}\/\d{2}\/\d{4}(?: \d{2}:\d{2}:\d{2})?$/.test(t)) return soloFecha ? t.slice(0, 10) : t;
  // SQL dates and timestamps without a zone are civil dates, not UTC instants.
  const civil = t.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}:\d{2}:\d{2})(?:\.\d+)?)?$/);
  if (civil) return `${civil[3]}/${civil[2]}/${civil[1]}` + (!soloFecha && civil[4] ? ' ' + civil[4] : '');
  const date = new Date(/^\d+$/.test(t) ? Number(t) : t);
  if (Number.isNaN(date.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'America/Santo_Domingo',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' }).formatToParts(date);
  const p = Object.fromEntries(parts.map(v => [v.type, v.value]));
  return `${p.day}/${p.month}/${p.year}` + (soloFecha ? '' : ` ${p.hour}:${p.minute}:${p.second}`);
}

function fechaVenta(r, p) {
  if (texto(p.fecha_venta)) return p.fecha_venta;
  const fecha = fechaDetalle(r.fecha, true);
  const hora = texto(r.hora_registro);
  if (/^\d{2}:\d{2}:\d{2}/.test(hora)) return [fecha, hora.slice(0, 8)].filter(Boolean).join(' ');
  return primero(fechaDetalle(r.hora_registro), fecha);
}

function turnoAbordado(r) {
  const fecha = fechaDetalle(r.fecha_turno, true);
  if (!fecha || !tieneValor(r.numero_turno) || !tieneValor(r.es_sentido_ida)) return '';
  return (esVerdadero(r.es_sentido_ida) ? 'I-' : 'V-') + fecha.replace(/\//g, '') + '-' + r.numero_turno;
}

export function detalleVenta(vista, datos = {}) {
  const r = datos.registro || {}, p = datos.presentacion || {};
  const ticket = vista === 'tickets';
  const cancelado = tieneValor(ticket ? r.activo : r.activado) && !esVerdadero(ticket ? r.activo : r.activado);
  const abordado = esVerdadero(r.abordado);
  const grupos = [];
  const venta = [
    campo(ticket ? 'Turno vendido' : 'Viaje', ticket ? r.numero_viaje : r.no_viaje),
    campo(ticket ? 'Fecha de venta' : 'Fecha de facturacion', fechaVenta(r, p)),
    campo('Caja', nombreCodigo(primero(p.nombre_caja_venta, r.nombre_caja), r.caja)),
    campo('Cajero/a', nombreCodigo(p.nombre_usuario_venta, r.cajero)),
    campo('Control', p.nombre_control, true),
  ];
  if (ticket) {
    grupos.push({ titulo: 'Datos del abordaje', campos: abordado ? [
      campo('Turno abordado', turnoAbordado(r)), campo('Fecha de abordaje', primero(p.fecha_abordaje, fechaDetalle(r.fecha_lectura_ticket))),
      campo('Ficha', r.ficha), campo('Chofer', r.chofer),
      campo('Caja', nombreCodigo(p.nombre_caja_abordaje, r.caja_lectura_ticket)),
      campo('Cajero/a', nombreCodigo(p.nombre_usuario_abordaje, r.usuario_lectura_ticket)),
    ] : [], vacio: abordado ? '' : 'Este ticket no ha sido abordado.' });
  } else {
    grupos.push({ titulo: 'Datos del envio', campos: [
      campo('Remitente', r.nombre_remitente), campo('Destinatario', r.nombre_destinatario),
      campo('Telefono remitente', r.telefono_remitente), campo('Telefono destinatario', r.telefono_destinatario),
      campo('Cedula remitente', r.cedula_remitente), campo('Ruta', r.ruta),
      campo('Valor declarado', dinero(r.valor_declarado)),
      campo('Tipo de envio', ({ P: 'Paquete', D: 'Dinero', R: 'Regalo' })[texto(r.tipo_envio).toUpperCase()] || r.tipo_envio),
      campo('Descripcion', r.descripcion, true),
    ] });
    venta.splice(2, 0, campo('Ficha', r.ficha), campo('Chofer', p.nombre_chofer));
  }
  grupos.push({ titulo: ticket ? 'Datos de venta' : 'Datos de facturacion', campos: venta });
  const precio = ticket ? r.precio : r.precio_envio;
  const descuento = r.descuento, cortesia = ticket ? r.valor_cortesia : r.cortesia;
  const recargo = ticket ? r.recargo : r.monto_recargo;
  const totales = [campo('Precio', dinero(precio)), campo('Descuento', dinero(descuento || 0))];
  if (ticket || numero(cortesia)) totales.push(campo('Cortesia', dinero(cortesia || 0)));
  totales.push(campo('Recargos', dinero(recargo || 0)));
  if (!ticket && numero(r.impuestos)) totales.push(campo('Impuestos', dinero(r.impuestos)));
  const total = tieneValor(r.precio_total) ? r.precio_total : ticket && numero(precio) !== null
    ? numero(precio) - (numero(descuento) || 0) - (numero(cortesia) || 0) + (numero(recargo) || 0) : null;
  const calculos = { PL: 'Precio por libra', PP: 'Precio por pies', PV: 'Precio por valor declarado', PA: 'Precio por asiento', PD: 'Precio digitado' };
  const pagos = { EFECTIVO: 'Efectivo', CREDITO: 'Credito', TARJETA: 'Tarjeta', CHEQUE: 'Cheque', TRANSFERENCIA: 'Transferencia', NOTA_CREDITO: 'Nota de credito', PAGO_DESTINO: 'Pago en destino' };
  const notas = ticket ? [campo('Motivo recargo', primero(r.motivo_recargo, numero(recargo) ? '' : 'Sin recargo'))]
    : [campo('Tipo cobro', calculos[texto(r.tipo_pago).toUpperCase()])];
  if (!ticket && pagos[r.tipo_pago]) notas.push(campo('Forma de pago', pagos[r.tipo_pago]));
  if (!ticket && texto(r.motivo_recargo)) notas.push(campo('Motivo recargo', r.motivo_recargo));
  return {
    numero: primero(ticket ? r.id : r.no_envio, r.id), ruta: ticket ? texto(r.ruta) : '',
    estado: cancelado ? 'CANCELADO' : ticket ? abordado ? 'ABORDADO' : 'NO ABORDADO'
      : esVerdadero(r.is_recogido) ? 'ENTREGADO' : esVerdadero(r.is_recibido) ? 'RECIBIDO' : 'NO RECIBIDO',
    cancelado, grupos, notas, totales, total: dinero(total),
  };
}
